import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Users } from "lucide-react";
import { fmtTs } from "@/components/Timeline";
import { TEAMS, ESC_TONE } from "@/lib/teams";

export function EscBadge({ e }) {
    if (!e) return null;
    return <span data-testid={`esc-badge-${e.ref}`} className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${ESC_TONE[e.status] || "bg-navy/5"}`}>{TEAMS[e.team]} · {e.status}</span>;
}

export function EscHistory({ e }) {
    return (
        <ol className="space-y-1.5" data-testid={`esc-history-${e.ref}`}>
            {(e.history || []).slice().reverse().map((h, i) => (
                <li key={i} className="rounded-lg bg-navy/[0.03] px-3 py-2 text-xs">
                    <p className="font-bold text-navy">{h.status === "Assigned" ? `Sent to ${TEAMS[h.team]}` : h.status} <span className="font-normal text-navy/45">· {h.by} · {fmtTs(h.at)}</span></p>
                    {h.note && <p className="mt-0.5 text-navy/70">{h.note}</p>}
                </li>
            ))}
        </ol>
    );
}

export function useEscalations(kind) {
    const [map, setMap] = useState({});
    const load = useCallback(() => {
        api.get(`/admin/escalations?kind=${kind}`).then((r) => setMap(Object.fromEntries(r.data.map((e) => [e.source_id, e])))).catch(() => {});
    }, [kind]);
    useEffect(() => { load(); }, [load]);
    return [map, load];
}

export function RouteToTeam({ kind, sourceId, esc, onDone }) {
    const [team, setTeam] = useState(esc?.team && esc.status !== "Sent back" ? esc.team : "accounts");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const send = async () => {
        setBusy(true);
        try { await api.post("/admin/escalations", { kind, source_id: sourceId, team, note }); toast.success(`Sent to ${TEAMS[team]}`); setNote(""); onDone(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div data-testid={`route-to-team-${sourceId}`} className="space-y-2 rounded-xl border border-orange/25 bg-orange/5 p-3">
            <div className="flex items-center justify-between">
                <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-navy/55"><Users size={13} /> Department routing <span className="font-normal normal-case text-navy/40">(internal, the customer doesn't see this)</span></p>
                <EscBadge e={esc} />
            </div>
            {esc && <EscHistory e={esc} />}
            <div className="flex flex-wrap gap-2">
                <select data-testid="route-team-select" value={team} onChange={(e) => setTeam(e.target.value)} className="rounded-full border border-navy/15 bg-white px-3 py-2 text-xs font-bold text-navy">
                    {Object.entries(TEAMS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input data-testid="route-team-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What should the team do? e.g. check refund status" className="min-w-[180px] flex-1 rounded-full border border-navy/15 bg-white px-3 py-2 text-xs outline-none focus:border-orange" />
                <button data-testid="route-team-send" disabled={busy} onClick={send} className="flex items-center gap-1 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white disabled:opacity-60"><Send size={12} /> {esc ? "Re-send" : "Send to team"}</button>
            </div>
        </div>
    );
}
